/**
 * Edge cue (UX-011).
 *
 * At the end of a row — and at the last rail, and at both ends of the hero — an arrow press did
 * *nothing*: no move, no animation, no sound, no hint. QA counted 8 consecutive ArrowDown
 * presses and 6 ArrowRight presses that changed not one pixel, which on a TV reads as "the
 * remote is broken", the exact complaint that produced the focus watchdog (docs/04 §2.1).
 *
 * The product decision (PR #22 left it open) is **not to wrap**: a list that jumps back to its
 * first item loses the user's place, and on a 143 k-song catalog the ends are meaningful. So the
 * press gets an answer instead: a short nudge in the direction of the press, on the rail/row the
 * focus is in, or on the focused element itself.
 *
 * How the "nothing happened" is detected: norigin has no "navigation failed" event, so this
 * module watches the current focus key and remembers *when it last changed*; a press whose turn
 * ends with no change since the key went down went nowhere. The listener is registered in the
 * capture phase so it runs before norigin's own — see {@link initEdgeCue} for why both halves
 * are needed (UX-300). Two presses that norigin itself drops are excluded: while navigation is
 * paused, and inside the key throttle window (`initTvNavigation({ throttle })`, 125 ms on the
 * TV) — those are not edges, they are presses the service never looked at.
 *
 * Cost on a low-end TV: one attribute write and a 220 ms transform-only CSS animation on one
 * element. No layout, no paint of anything else, and nothing at all under
 * `prefers-reduced-motion` (see `styles/index.css`).
 */
export type EdgeDirection = 'left' | 'right' | 'up' | 'down';

/** Attribute the animation hangs off; its value is the direction of the refused press. */
export const EDGE_CUE_ATTR = 'data-edge-cue';
/**
 * Marks the element that should play the cue for the focusables inside it — the row, the rail,
 * the hero. Without one the focused element nudges by itself, which is right for a standalone
 * button or a list row.
 */
export const EDGE_CUE_TARGET_ATTR = 'data-edge-cue-target';
/** Must match the animation duration in `styles/index.css`. */
export const EDGE_CUE_MS = 220;

const KEY_DIRECTIONS: Readonly<Record<string, EdgeDirection>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
  Left: 'left',
  Right: 'right',
  Up: 'up',
  Down: 'down',
};

const CODE_DIRECTIONS: Readonly<Record<number, EdgeDirection>> = {
  37: 'left',
  38: 'up',
  39: 'right',
  40: 'down',
};

/** The direction an event asks for, or null when it is not one of the four arrows. */
export function directionOf(event: Pick<KeyboardEvent, 'key' | 'keyCode'>): EdgeDirection | null {
  return KEY_DIRECTIONS[event.key] ?? CODE_DIRECTIONS[event.keyCode] ?? null;
}

/** The focused leaf, as the components mark it (docs/04 §2.1). */
export function focusedElement(doc: Document = document): HTMLElement | null {
  return doc.querySelector<HTMLElement>('[data-focus-key][data-focused="true"]');
}

/**
 * Which element plays the cue for a focused leaf: the nearest ancestor that claimed the job,
 * else the leaf itself.
 */
export function edgeCueTarget(focused: Element | null): HTMLElement | null {
  if (!focused || !(focused instanceof HTMLElement)) return null;
  const owner = focused.closest<HTMLElement>(`[${EDGE_CUE_TARGET_ATTR}]`);
  return owner ?? focused;
}

/** Plays the nudge on one element. Re-arming it restarts the animation. */
export function playEdgeCue(el: HTMLElement | null, direction: EdgeDirection): void {
  if (!el) return;
  const pending = timers.get(el);
  if (pending !== undefined) {
    clearTimeout(pending);
    el.removeAttribute(EDGE_CUE_ATTR);
    // Reading a layout value between the two writes is what makes a repeated press replay the
    // animation instead of being ignored as "the attribute is already there".
    void el.offsetWidth;
  }
  el.setAttribute(EDGE_CUE_ATTR, direction);
  timers.set(
    el,
    setTimeout(() => {
      timers.delete(el);
      el.removeAttribute(EDGE_CUE_ATTR);
    }, EDGE_CUE_MS) as unknown as number,
  );
}

const timers = new Map<HTMLElement, number>();

export interface EdgeCueOptions {
  /** Same value `initTvNavigation` gave norigin; presses inside it never reach the service. */
  throttleMs?: number;
  /** Reads norigin's paused flag (see `initTvNavigation`). */
  isPaused?: () => boolean;
  /** Current focus key; the cue fires only when it is the same before and after the press. */
  getFocusKey: () => string | null;
}

let dispose: (() => void) | null = null;

/**
 * Installs the window-level detector. Safe to call repeatedly: the previous installation is
 * removed first (HMR, tests, a second `initTvNavigation`).
 */
export function initEdgeCue(options: EdgeCueOptions): void {
  disposeEdgeCue();
  const { throttleMs = 0, isPaused, getFocusKey } = options;
  let lastAt = 0;

  /**
   * Why the listener is registered in the **capture** phase (UX-300).
   *
   * `initTvNavigation` calls norigin's `init()` *before* `initEdgeCue`, so norigin's own
   * `keydown` listener is registered first and — in a browser, where it navigates synchronously
   * — has already moved the focus by the time a listener registered later runs. The "before"
   * snapshot taken there was really an "after" snapshot, the comparison one turn later could
   * never see a difference, and the cue fired on **every** arrow press: 43 of 43 on Home, 30 of
   * 30 in search, so it signalled nothing and ran a 220 ms transform on every press of a D-pad
   * session. A capture-phase listener on `window` runs before any bubble-phase one for an event
   * whose target is an element — which every real key press is — so the snapshot is taken
   * before norigin looks at the key, whatever order the two were installed in.
   */
  const onKeyDown = (event: KeyboardEvent) => {
    const direction = directionOf(event);
    if (!direction || isPaused?.()) return;
    const now = Date.now();
    // Throttled away by norigin: not an edge, just a press it never saw.
    if (throttleMs > 0 && now - lastAt < throttleMs) return;
    lastAt = now;
    const before = getFocusKey();
    if (!before) return;
    // Norigin moves the focus inside its own (async) scheduler, so the comparison waits a turn.
    setTimeout(() => {
      if (getFocusKey() !== before) return;
      playEdgeCue(edgeCueTarget(focusedElement()), direction);
    }, 0);
  };

  window.addEventListener('keydown', onKeyDown, true);
  dispose = () => window.removeEventListener('keydown', onKeyDown, true);
}

export function disposeEdgeCue(): void {
  dispose?.();
  dispose = null;
  for (const [el, timer] of timers) {
    clearTimeout(timer);
    el.removeAttribute(EDGE_CUE_ATTR);
  }
  timers.clear();
}
